import React, { useEffect, useState, useMemo, memo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import messages from 'i18n/messages/home';
import moment from 'moment';
import { useGetEnumOptions } from 'pages/qa-spec/hooks';
import classnames from 'classnames';
import {
  Col,
  Row,
  Typography,
  Radio,
  Space,
  Divider,
  Tooltip,
  Input,
  Select,
  Empty,
  Form
} from 'antd';
import { InfoCircleOutlined } from "@ant-design/icons";
import { isEmpty, sortBy, isEqual } from 'lodash';
import { checkShowPdfPreview } from 'utils/checkShowPdfPreview';
import { EditContainer } from 'pages/qa-spec/components';
import {
  CustomNotification,
  dialogFunction,
  FormAddButton,
  FormDeleteButton,
  PreviewableModal,
  StyledModal,
  WithLoading,
  WrapperSelect,
  Form,
  SectionWrapper,
  TargetMarketInput,
} from 'common/components';
import { COMMENT_FIELDS } from 'static/QaSpecFields';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import {
  MINIMUM_INPUT_FIELD,
  PRODUCT_SPEC_FORM_SECTION_SCHEMA,
} from '../../constant';

import {
  EditTabSectionWrapper,
  EDIT_SECTION,
} from 'pages/qa-spec/components/edit-tab-checker';

import {
  useGetQaClaimsTabData,
  useSaveQaClaimsTab,
  useGetReadyToUseClaim,
} from './queries';
import { useGetSnapshotQaClaims } from './hooks';
import { useCheckSnapshotForRetailer } from 'pages/qa-spec/hooks';

import { useGetQaSpecRequiredFieldsInQaSpecFullView } from 'pages/qa-spec/hooks/useGetQaSpecRequiredFields';
import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import { useSaveClaimsVisibilityProductLevel, useDeleteClaimsVisibilityProductLevel } from 'hooks/qas-require-field/useQasRequireField';

import { useGetEnumsByEnumName } from 'hooks';
import { CellRenderActionClaims } from 'pages/company-profile/components/tabs/contacts/companyCertificate/hook';

import sortByKey from 'utils/sortByKey';

import {
  generateFormValuesClaims,
  mapClaimsEnumByValue,
  mapObjectToArrayWithKey,
  transformDatetimeForFormValues,
} from 'pages/qa-spec/components/qa-claims/utils';

import { addUtcForIsoString } from 'utils/timezone';
import { formatMDY } from 'utils/formatDate';

import { QaClaimsSnapshot } from 'pages/qa-spec/components/qa-claims/QaClaimsSnapshot';

import {
  HighlightContent,
  PrimaryFieldSnapshot,
} from 'pages/qa-spec/components/qa-prod-spec/snapshot/RetailerSnapshot';
import Messages from 'i18n/messages/qa-spec';
const { Text } = Typography;
import './QaClaims.less';

const QaClaims = ({ isTabActive, ownerId }) => {
  const intl = useIntl();
  const [addClaim, setAddClaim] = useState(false);
  const editHook = useState(false);
  const [isEdit, setIsEdit] = editHook;
  const { id: productId } = useParams();
  const [formInst] = Form.useForm();
  const isToggleSnapshot = useCheckSnapshotForRetailer();
  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productId,
    isEnabled: Boolean(productId),
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const [stateActions, setStateActions] = useState({
    visible: false,
    filedata: null,
  });

  const previewMeta = useMemo(() => {
    const fd = stateActions.filedata;

    if (!fd) return { id: '', title: '', link: null, extension: null };

    const fileType =
      normalizeExt(fd?.certificateType) ||
      extractExtFromUrl(fd?.certificateThumbSizeFull) ||
      extractExtFromUrl(fd?.certificateUrl) ||
      normalizeExt(fd?.fileExtension || fd?.fileType);

    const pdfPreview =
      fd?.pdfPreview ||
      fd?.certificateThumbSizeFull ||
      fd?.certificateThumbSize2500 ||
      fd?.certificateThumbSize1000 ||
      fd?.certificateThumbSize500;

    const { linkPreview, extensionFile } = checkShowPdfPreview(
      pdfPreview,
      fd?.certificateUrl,
      fileType
    );

    return {
      id: fd?.certifierName || 'certificate',
      title: fd?.certifierName || 'Certificate',
      link: linkPreview,
      extension: extensionFile,
    };
  }, [stateActions.filedata]);


  const qaSpecType = qaSpecDataHeader?.type;

  const { isLoading: isLoadingSnapshotQaClaims, snapshotObjectClaims } =
    useGetSnapshotQaClaims({
      enabled: !!productId && isToggleSnapshot,
      productId,
      productType: qaSpecType,
    });

  const {
    data,
    isLoading: loading,
    isFetching,
    invalidateClaims,
    objectClaims,
  } = useGetQaClaimsTabData(
    { productId, productType: qaSpecType },
    {
      enabled: !!productId && !!qaSpecType,
      retry: false,
      staleTime: 5 * 60 * 1000,
    }
  );
  useEffect(() => {
    if (!productId || !qaSpecType) return;
    setClaimsState([]);
    formInst.resetFields();
    invalidateClaims();
  }, [qaSpecType, productId]);

  const [claimsState, setClaimsState] = useState([]);
  const deleteClaimsVisibilityMutation = useDeleteClaimsVisibilityProductLevel();

  const handleSaveQaClaimsTab = useSaveQaClaimsTab();
  const { isLoading: saveLoading } = handleSaveQaClaimsTab;

  useEffect(() => {
    if (claimsState || claimsState.length > 0 || !isDeleting) {
      const formData = generateFormValuesClaims(claimsState || []);
      const currentFormValues = formInst.getFieldsValue();
      const pfasClaim = data?.pfasClaim || {};
      if (isEmpty(currentFormValues)) {
        formInst.setFieldsValue({
          ...formData,
          ...pfasClaim,
          contains: data?.contains || [],
          doesNotContain: data?.doesNotContain || [],
        });
      } else {
        let newFormValues = {};
        for (const [key, value] of Object.entries(formData)) {
          if (currentFormValues[key]) {
            newFormValues[key] = {
              claimTypeCode: key,
              isAttribute:
                value.isAttribute !== undefined
                  ? value.isAttribute
                  : currentFormValues[key]?.isAttribute,
              willCustomerFacing:
                value.willCustomerFacing !== undefined
                  ? value.willCustomerFacing
                  : currentFormValues[key]?.willCustomerFacing,
              isThirdParty:
                value.isThirdParty !== undefined
                  ? value.isThirdParty
                  : currentFormValues[key]?.isThirdParty,
              additionalInformation:
                value.additionalInformation !== undefined
                  ? value.additionalInformation
                  : currentFormValues[key]?.additionalInformation,
              certifierName:
                value.certifierName !== undefined
                  ? value.certifierName
                  : currentFormValues[key]?.certifierName,
              certificationExpiration:
                value.certificationExpiration !== undefined
                  ? value.certificationExpiration
                  : currentFormValues[key]?.certificationExpiration,
              certificateUrl:
                value.certificateUrl !== undefined
                  ? value.certificateUrl
                  : currentFormValues[key]?.certificateUrl,
              certifierName_ReadyToUse:
                value.certifierName_ReadyToUse !== undefined
                  ? value.certifierName_ReadyToUse
                  : currentFormValues[key]?.certifierName_ReadyToUse,
              certificationExpiration_ReadyToUse:
                value.certificationExpiration_ReadyToUse !== undefined
                  ? value.certificationExpiration_ReadyToUse
                  : currentFormValues[key]?.certificationExpiration_ReadyToUse,
              certificateUrl_ReadyToUse:
                value.certificateUrl_ReadyToUse !== undefined
                  ? value.certificateUrl_ReadyToUse
                  : currentFormValues[key]?.certificateUrl_ReadyToUse,
              certificateThumbSizeFull:
                value.certificateThumbSizeFull !== undefined
                  ? value.certificateThumbSizeFull
                  : currentFormValues[key]?.certificateThumbSizeFull,
              pdfPreview:
                value.pdfPreview !== undefined
                  ? value.pdfPreview
                  : currentFormValues[key]?.pdfPreview
            };
          } else {
            newFormValues[key] = value;
          }
        }
        formInst.setFieldsValue({
          ...newFormValues,
          ...pfasClaim,
          contains: data?.contains || [],
          doesNotContain: data?.doesNotContain || [],
        });
      }
    } else {
      const formData = generateFormValuesClaims(claimsState || []);
      const pfasClaim = data?.pfasClaim || {};
      formInst.setFieldsValue({
        ...formData,
        ...pfasClaim,
        contains: data?.contains || [],
        doesNotContain: data?.doesNotContain || [],
      });
    }
  }, [claimsState, isLoadingSnapshotQaClaims, formInst, data?.pfasClaim]);

  const handleDeleteClaim = (deletedClaim) => {
    dialogFunction({
      type: 'warn',
      content: 'Are you sure you want to delete this claim?',
      onOk: async () => {
        const params = {
          productId,
          productType: qaSpecType,
          visibleClaims: [deletedClaim],
        };

        try {
          setIsDeleting(true);
          const currentFormValues = formInst.getFieldsValue(true);
          const response = await deleteClaimsVisibilityMutation.mutateAsync(params);
          if (response?.isSuccess) {
            setClaimsState((prev) => {
              return prev ? prev.filter((c) => c.claimTypeCode !== deletedClaim) : [];
            });
            const newFormValues = { ...currentFormValues };
            delete newFormValues[deletedClaim];
            formInst.setFieldsValue(newFormValues);
            formInst.resetFields([deletedClaim]);
            setTimeout(() => {
              setIsDeleting(false);
            }, 100);
            CustomNotification.success('Claim deleted successfully');
          } else {
            CustomNotification.error(response?.message || 'Something went wrong!');
            setIsDeleting(false);
          }
        } catch (error) {
          CustomNotification.error(error?.message || 'Something went wrong!');
          setIsDeleting(false);
        }
      },
    });
  };

  const handleSave = async (toggleEditMode) => {
    try {
      // const formData = await formInst.validateFields();
      const formData = formInst.getFieldsValue();
      delete formData.Contains;
      delete formData['Does Not Contain'];

      const pfasFieldKeys =
        PRODUCT_SPEC_FORM_SECTION_SCHEMA.POLYFLUOROALKYL_SUBSTANCES.fields.map(
          (field) => field.name
        );
      pfasFieldKeys.push('contains');
      pfasFieldKeys.push('doesNotContain');

      const filteredFormData = Object.fromEntries(
        Object.entries(formData).filter(([key]) => !pfasFieldKeys.includes(key))
      );

      const contains = Object.fromEntries(
        Object.entries(formData).filter(([key]) => key === 'contains')
      );

      const doesNotContain = Object.fromEntries(
        Object.entries(formData).filter(([key]) => key === 'doesNotContain')
      );
      pfasFieldKeys.pop('contains');
      pfasFieldKeys.pop('doesNotContain');
      const pfasClaim = Object.fromEntries(
        Object.entries(formData).filter(([key]) => pfasFieldKeys.includes(key))
      );

      const params = {
        productId,
        productType: qaSpecDataHeader?.type,
        claims: mapObjectToArrayWithKey(filteredFormData),
        pfasClaim,
        ...contains,
        ...doesNotContain,
      };

      handleSaveQaClaimsTab.mutate(params, {
        onSuccess: () => {
          invalidateClaims();
          toggleEditMode();
        },
      });
    } catch (error) {
      CustomNotification.error('Please resolve error(s)');
    }
  };

  const handleCancel = () => {
    formInst.resetFields();

    let mappingClaims = data?.claims?.map((claim) => ({
      ...claim,
    }));

    const claimsValues = generateFormValuesClaims(mappingClaims || []);
    const pfasClaim = data?.pfasClaim || {};

    formInst.setFieldsValue({
      ...claimsValues,
      ...pfasClaim,
      contains: data?.contains || [],
      doesNotContain: data?.doesNotContain || [],
    });
  };

  let requiredClaims = useGetEnumsByEnumName('OptionalClaims');
  requiredClaims = mapClaimsEnumByValue(requiredClaims);


  const visibleClaims = useMemo(
    () => (claimsState || []).map((claim) => claim.claimTypeCode),
    [claimsState]
  );

  useEffect(() => {
    if (!Array.isArray(data?.claims)) return;
    setClaimsState((prev) => {
      if (prev.length === 0 && data.claims.length === 0) return prev;
      if (!isEqual(prev, data.claims)) {
        return data.claims;
      }
      return prev;
    });
  }, [data?.claims]);

  const renderSection = (isEdit, section, formInst, isToggleSnapshot, snapshotObjectClaims, objectClaims, requiredFields) => {
    return (
      <SectionWrapper
        className='qa-spec-product-spec__info-section'
        title={intl.formatMessage(Messages[section.key].title)}
      >
        <Row>{renderField(isEdit, section.fields, section.key, formInst, requiredFields)}</Row>
      </SectionWrapper>
    );
  };

  const renderField = (isEdit, fields, sectionKey, formInst, requiredFields) => {
    const minimumInputNumber = MINIMUM_INPUT_FIELD.includes(sectionKey)
      ? 0
      : undefined;

    const inputCollection = {
      text: <PrimaryFieldSnapshot variant='text' readOnly={!isEdit} />,
      checkbox: <PrimaryFieldSnapshot variant='checkbox' disabled={!isEdit} />,
      textarea: (params) => {
        const { inputProps } = params || {};

        return (
          <PrimaryFieldSnapshot
            variant='textarea'
            {...inputProps}
            readOnly={!isEdit}
          />
        );
      },
    };
    const checkFieldRequired = (fieldName) => {
      if (isEmpty(requiredFields) || !fieldName) return false;
      const downCasedFieldName = fieldName.toLowerCase();
      const isNormalFieldRequired = requiredFields.includes(
        `qaspecification.qaspecclaimsrequired.${downCasedFieldName}`
      );
      return isNormalFieldRequired;
    };

    return fields.map((field) => {
      const label = intl.formatMessage(Messages?.[sectionKey]?.[field.name]);
      const { inputRender } = field;
      const isRequiredField = checkFieldRequired(
        field?.name
      );

      const rules =
        isRequiredField
          ? [
            {
              required: true,
              message: `${label || 'This field'} is required`,
            },
          ]
          : null;
      const InputField = inputRender || inputCollection[field.type];
      const formItemProps = field?.formItemProps || {};
      return (
        <Col span={field.col || 6}>
          {field.isList ? (
            renderFormList(isEdit, field, sectionKey)
          ) : (
            <Form.Item
              key={field.name}
              name={field.name}
              label={
                <TraceableCommentContainer
                  qaSpecName={'Claims'}
                  qaSpecFieldName={label}
                >
                  <QaSpecHelpContentLabel fieldPath={field.fieldPath}>
                    <Text ellipsis={{ tooltip: <strong>{label}</strong> }}>
                      <strong>{label}</strong>
                    </Text>
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              }
              rules={rules}
              {...formItemProps}
            >
              {typeof InputField === 'function'
                ? InputField({ ...field, isEdit })
                : InputField}
            </Form.Item>
          )}
        </Col>
      );
    });
  };


  const AddClaim = ({ ...props }) => {
    const [selectedClaims, setSelectedClaims] = useState([]);
    const optionalClaims = useGetEnumsByEnumName('OptionalClaims');

    const filteredClaims = optionalClaims.filter(
      (claim) => !visibleClaims.includes(claim.enumDisplayName)
    );

    const claimsVisibilityMutation = useSaveClaimsVisibilityProductLevel();

    const handleAddClaims = () => {
      const newVisibleClaims = visibleClaims.concat(selectedClaims);
      const params = {
        productId,
        productType: qaSpecType,
        visibleClaims: selectedClaims,
      };

      claimsVisibilityMutation.mutate(params, {
        onSuccess: (response) => {
          if (response?.isSuccess) {
            CustomNotification.success('Claims added successfully');
            invalidateClaims();
            setSelectedClaims([]);
            props.onCancel?.();
          } else {
            CustomNotification.error(
              response?.message || 'Something went wrong!'
            );
          }
        },
      });
    };

    return (
      <StyledModal
        title='Add Additional Claims'
        width={600}
        okText='Add'
        maskClosable={false}
        onOk={handleAddClaims}
        onButtonProps={{
          loading: claimsVisibilityMutation.isLoading,
        }}
        {...props}
      >
        <WrapperSelect
          mode='multiple'
          style={{
            width: '80%',
          }}
          placeholder='Select claims'
          onSelect={(value) => {
            setSelectedClaims((prev) => [...prev, value]);
          }}
          onClear={() => {
            setSelectedClaims([]);
          }}
          onDeselect={(value) => {
            setSelectedClaims((prev) =>
              prev.filter((claim) => claim !== value)
            );
          }}
        >
          {sortByKey(filteredClaims, 'enumDisplayName').map((option) => (
            <ClaimOption
              key={option.enumDisplayName}
              value={option.enumDisplayName}
              label={option.enumDisplayName}
            />
          ))}
        </WrapperSelect>
      </StyledModal>
    );
  };

  const { data: requiredFields } = useGetQaSpecRequiredFieldsInQaSpecFullView({
    productId,
  });

  const claimsEnumOptions = useGetEnumOptions({
    optionMapping: useMemo(
      () => (option) => ({
        value: option.enumDisplayName,
        displayName: option.enumDisplayName,
      }),
      []
    ),
  })?.claims;

  const prevStatesRef = useRef({ isEdit: false, isTabActive: false });

  useEffect(() => {
    const wasTabActive = prevStatesRef.current.isTabActive;
    const nowTabActive = isTabActive;
    if ((!wasTabActive && nowTabActive)) {
      // invalidateSetupOptionalClaims();
      invalidateClaims();
    }
    prevStatesRef.current = { isTabActive: nowTabActive };
  }, [isTabActive]);


  const isClaimRequired = (claimTypeCode) => {
    if (!requiredFields) return false;
    const downCased = requiredClaims[claimTypeCode]?.enumValue.toLowerCase();
    return requiredFields.includes(`qaspecification.qaspecclaimsrequired.${downCased}`);
  };

  const sortedClaims = [...claimsState].sort((a, b) => {
    const aReq = isClaimRequired(a.claimTypeCode);
    const bReq = isClaimRequired(b.claimTypeCode);
    if (aReq && !bReq) return -1;
    if (!aReq && bReq) return 1;
    return a.claimTypeCode.localeCompare(b.claimTypeCode);
  });


  return (
    <Form
      className='qa-claims'
      form={formInst}
      style={{ paddingRight: 5 }}
      layout='horizontal'
      labelCol={{ span: 12 }}
      wrapperCol={{ span: 12 }}
    >
      <WithLoading
        loading={
          loading || isFetching || saveLoading
        }
      >
        <EditContainer
          type={EDIT_SECTION.QA_CLAIMS}
          productId={productId}
          handleCancel={handleCancel}
          handleSave={handleSave}
          editHook={editHook}
          buttons={[
            <FormAddButton
              text='Add Value'
              onClick={() => setAddClaim(true)}
            />,
          ]}
          float
        >
          {(isEdit) => {
            return (
              <>
                <EditTabSectionWrapper.CheckerWrapper
                  isEdit={isEdit}
                  type={EDIT_SECTION.QA_CLAIMS}
                >
                  <SectionWrapper className='qa-spec-product-spec__info-section'>
                    {!loading && (
                      <div
                        style={{
                          maxHeight: '400px',
                          overflowY: 'auto',
                          padding: '0 10px',
                        }}
                      >
                        <div className='qa-claims__header-container'>
                          <Row
                            gutter={[8, 8]}
                            className={classnames('qa-claims__input', {
                              'qa-claims__input--edit': isEdit,
                            })}
                          >
                            <HeaderSectionClaim />
                          </Row>
                        </div>

                        <Row gutter={[8, 8]} className={classnames('qa-claims__input', { 'qa-claims__input--edit': isEdit })}>
                          {sortedClaims.length > 0 ? (
                            sortedClaims.map((item) => (
                              <React.Fragment key={item.claimTypeCode}>
                                <HeaderClaimItem
                                  label={item.claimTypeCode}
                                  value={requiredClaims[item.claimTypeCode]?.enumValue}
                                  productId={productId}
                                  isEdit={isEdit}
                                  productType={qaSpecType}
                                  visibleClaims={visibleClaims}
                                  invalidateClaims={invalidateClaims}
                                  isToggleSnapshot={isToggleSnapshot}
                                  snapshotValue={snapshotObjectClaims?.[item.claimTypeCode]}
                                  supplierModifiedValue={objectClaims?.[item.claimTypeCode]}
                                  formInst={formInst}
                                  setStateActions={setStateActions}
                                  stateActions={stateActions}
                                  requiredFields={requiredFields}
                                  claimsEnumOptions={claimsEnumOptions}
                                  productLevelClaims={[]}
                                  canDelete={item.canDelete}
                                  certificateCount={item.certificateCount}
                                  onDelete={handleDeleteClaim}
                                />
                                <Col span={24}>
                                  <Divider style={{ margin: '8px 0' }} />
                                </Col>
                              </React.Fragment>
                            ))
                          ) : (
                            <Col span={24} >
                              <Empty />
                            </Col>
                          )}

                        </Row>

                        <PreviewableModal
                          key={`${previewMeta.extension}-${previewMeta.link}`}
                          id={previewMeta.id}
                          link={previewMeta.link}
                          title={previewMeta.title}
                          visible={stateActions.visible}
                          fileName={previewMeta.title}
                          cancelModal={() => setStateActions({ visible: false, filedata: null })}
                          afterClose={() => setStateActions({ visible: false, filedata: null })}
                          downloadLink={previewMeta.link}
                          fileType={previewMeta.extension}
                          isDocument={true}
                          assetData={stateActions.filedata}
                        />

                      </div>


                    )}
                    {/* <div>
                      <Row
                        gutter={[8, 8]}
                        className={classnames('qa-claims__input', {
                          'qa-claims__input--edit': isEdit,
                        })}
                      >
                        <Col span={6}>
                          <div>
                            <BasicFormItemList
                              allowEdit={isEdit}
                              formDefine={formDefine}
                              labelFn={labelFn}
                              isLoadingSnapshot={isLoadingSnapshotQaClaims}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div> */}
                  </SectionWrapper>

                  {addClaim && (
                    <AddClaim
                      visible={addClaim}
                      onCancel={() => setAddClaim(false)}
                      productId={productId}
                      visibleClaims={visibleClaims}
                      productType={qaSpecType}
                      formInst={formInst}
                      invalidateClaims={invalidateClaims}
                      data={data}
                    />
                  )}
                </EditTabSectionWrapper.CheckerWrapper>
                <Col span={12} xxl={24} style={{ marginBottom: '1em' }}>
                  {renderSection(
                    isEdit,
                    PRODUCT_SPEC_FORM_SECTION_SCHEMA.POLYFLUOROALKYL_SUBSTANCES,
                    formInst,
                    isToggleSnapshot,
                    snapshotObjectClaims,
                    objectClaims,
                    requiredFields
                  )}
                </Col>
              </>
            );
          }}
        </EditContainer>
      </WithLoading>
    </Form>
  );
};

const HeaderSectionClaim = () => {
  return (
    <>
      <Col span={4}>
        <HeaderClaim text='Claims' />
      </Col>
      <Col span={3}>
        <HeaderClaim text='Is this an Attribute for the product?' />
      </Col>
      <Col span={3}>
        <HeaderClaim text={
          <>
            Will claim be customer<br />facing (Label or Online?)
          </>
        } />
      </Col>
      <Col span={3}>
        <HeaderClaim
          text={
            <Typography.Title
              level={5}
              style={{ fontSize: 14, color: '#595959' }}
            >
              Is 3<sup style={{ fontSize: 12 }}>rd</sup> party certified?
            </Typography.Title>
          }
        />
      </Col>
      <Col span={3}>
        <HeaderClaim text='Certificate Name' />
      </Col>
      <Col span={3}>
        <HeaderClaim text={<>Certification Expiration <br />Date (if applicable)'</>} />
      </Col>
      <Col span={2}>
        <HeaderClaim text={<>Certification <br />Link</>} />
      </Col>
      <Col span={3}>
        <HeaderClaim text='Additional Information' />
      </Col>
    </>
  );
};

const HeaderClaimItem = memo(
  ({
    label,
    value,
    productId,
    productType,
    isEdit,
    invalidateClaims,
    isToggleSnapshot,
    supplierModifiedValue,
    snapshotValue,
    formInst,
    setStateActions,
    requiredFields,
    children,
    claimsEnumOptions = {},
    canDelete = false,
    certificateCount,
    onDelete
  }) => {
    const requiredClaims = useGetEnumsByEnumName('OptionalClaims');

    // const allSetupClaims = setupOptionalClaims;
    const [applyTick, setApplyTick] = useState(0);
    const {
      data: readyCertData,
      isFetching: isFetchingReadyClaim,
      refetch: fetchReadyToUseClaim,
      invalidateReadyToUseClaims,
    } = useGetReadyToUseClaim({ productId, productType, claimTypeCode: label });

    useEffect(() => {
      if (!applyTick) return;
      if (!readyCertData) return;

      const current = formInst.getFieldValue(label) || {};

      formInst.setFieldsValue({
        [label]: {
          ...current,
          certifierName: readyCertData.certifierName_ReadyToUse ?? null,
          certificationExpiration:
            readyCertData.certificationExpiration_ReadyToUse ?? null,
          certificateUrl: readyCertData.certificateUrl_ReadyToUse ?? null,
          certificateThumbSizeFull:
            readyCertData.certificateThumbSizeFull ?? null,
          pdfPreview: readyCertData.pdfPreview ?? null,
          certificateType: readyCertData.certificateType ?? null,
        },
      });
      if (!readyCertData.certifierName_ReadyToUse && !readyCertData.certificationExpiration_ReadyToUse && !readyCertData.certificateUrl_ReadyToUse) {
        CustomNotification.info('Please update ' + label + ' certificate for this product.');
      }
    }, [applyTick, readyCertData, label]);


    // const claimsVisibilityMutation = useDeleteClaimsVisibilityProductLevel();


    const checkFieldRequired = (fieldName) => {
      if (isEmpty(requiredFields) || !fieldName) return false;

      const downCasedFieldName = fieldName.toLowerCase();

      const isNormalFieldRequired = requiredFields.includes(
        `qaspecification.qaspecclaimsrequired.${downCasedFieldName}`
      );
      // const isTdcFieldRequired = requiredFields.includes(
      //   `tdcproperties.${downCasedFieldName}`
      // );
      return isNormalFieldRequired; //|| isTdcFieldRequired;
    };

    const getRequiredRule = ({ fieldName, displayName }) => {
      return {
        required: checkFieldRequired(fieldName),
        message: (displayName || 'This field') + ' is required',
      };
    };

    const isRequired = checkFieldRequired(value);

    const [modalVisible, setModalVisible] = useState(false);

    // --- Special case: Contains / Does Not Contain ---
    const isContainsField =
      label === 'Contains' || label === 'Does Not Contain';
    if (isContainsField) {
      const fieldName = label === 'Contains' ? 'contains' : 'doesNotContain';
      // const options = claimsEnumOptions?.[fieldName] || [];
      const options =
        label === 'Contains'
          ? sortBy(claimsEnumOptions?.contains || [], ['displayName'])
          : sortBy(claimsEnumOptions?.doesNotContain || [], ['displayName']);

      return (
        <>
          <Col span={4}>
            <Row style={{ marginBottom: 8 }} wrap={false} align='middle'>
              <Col flex='auto'>
                <TraceableCommentContainer
                  qaSpecName={'Claims'}
                  qaSpecFieldName={label}
                >
                  <QaSpecHelpContentLabel fieldPath={'qaSpecification.claimsDetails.claims.' + value}>
                    <Typography.Text strong ellipsis={{ tooltip: label }}>
                      {isRequired && (
                        <Typography.Text
                          type='danger'
                          style={{ fontSize: 14, paddingRight: 4 }}
                        >
                          *
                        </Typography.Text>
                      )}
                      {label}
                    </Typography.Text>
                  </QaSpecHelpContentLabel>
                </TraceableCommentContainer>
              </Col>
              {!isRequired && canDelete && (
                <Col flex='30px'>
                  <FormDeleteButton
                    disabled={!isEdit}
                    text={null}
                    onClick={() => onDelete(label)}
                  />
                </Col>
              )}
            </Row>
          </Col>

          <Col span={6}>
            <QaClaimsSnapshot
              isToggleSnapshot={isToggleSnapshot}
              supplierModifiedValue={supplierModifiedValue}
              snapshotValue={snapshotValue}
              qaSpecType={productType}
              productId={productId}
              highlightContent={
                <HighlightContent
                  label={label}
                  value={snapshotValue}
                  variant='text'
                />
              }
            >
              <Form.Item
                name={fieldName}
                wrapperCol={{ span: 24 }}
                rules={[
                  getRequiredRule({
                    fieldName: value,
                    displayName: label,
                  }),
                ]}
              >
                <PrimaryFieldSnapshot
                  variant='select'
                  mode='multiple'
                  style={{ width: '100%' }}
                  placeholder={`Select ${label.toLowerCase()} claims`}
                  optionFilterProp='children'
                  disabled={!isEdit}
                  allowClear
                  showSearch
                >
                  {options.map((opt) => {
                    const optValue =
                      opt.value ?? opt.enumDisplayName ?? opt.displayName;
                    const optLabel =
                      opt.displayName ?? opt.enumDisplayName ?? opt.value;
                    return (
                      <Select.Option key={optValue} value={optValue}>
                        {optLabel}
                      </Select.Option>
                    );
                  })}
                </PrimaryFieldSnapshot>
              </Form.Item>
            </QaClaimsSnapshot>
          </Col>
          <Col span={15}></Col>
        </>
      );
    }
    return (
      <>
        <Col span={4}>
          <Row style={{ marginBottom: 8 }} wrap={false} align='middle'>
            <Col flex='auto'>
              <TraceableCommentContainer
                qaSpecName={'Claims'}
                qaSpecFieldName={label}
              >
                <QaSpecHelpContentLabel fieldPath={'qaSpecification.claimsDetails.claims.' + value}>
                  <Typography.Text strong ellipsis={{ tooltip: label }}>
                    {isRequired && (
                      <Typography.Text
                        type='danger'
                        style={{ fontSize: 14, paddingRight: 4 }}
                      >
                        *
                      </Typography.Text>
                    )}
                    {label}
                  </Typography.Text>
                </QaSpecHelpContentLabel>
              </TraceableCommentContainer>
            </Col>
            {!isRequired && canDelete && (
              <Col flex='30px'>
                <FormDeleteButton
                  disabled={!isEdit}
                  text={null}
                  onClick={() => onDelete(label)}
                />
              </Col>
            )}
          </Row>
        </Col>

        <Col span={3}>
          <QaClaimsSnapshot
            isToggleSnapshot={isToggleSnapshot}
            supplierModifiedValue={supplierModifiedValue?.isAttribute}
            snapshotValue={snapshotValue?.isAttribute}
            qaSpecType={productType}
            productId={productId}
            highlightContent={
              <HighlightContent
                label='Is this an Attribute for the product?:'
                value={snapshotValue?.isAttribute}
                variant='checkbox-true-false'
              />
            }
          >
            <Form.Item
              name={[label, 'isAttribute']}
              wrapperCol={{ span: 24 }}
              rules={[
                getRequiredRule({
                  fieldName: value,
                  displayName: label,
                }),
              ]}
            >
              <PrimaryFieldSnapshot
                disabled={!isEdit}
                variant='checkbox-true-false'
                onChange={(e) => {
                  const checked = e.target.value;
                  if (!checked) {
                    const current = formInst.getFieldValue(label) || {};
                    formInst.setFieldsValue({
                      [label]: {
                        ...current,
                        willCustomerFacing: null,
                        isThirdParty: null,
                        certifierName: null,
                        certificationExpiration: null,
                        certificateUrl: null,
                        certificateThumbSizeFull: null,
                      },
                    });
                  }
                }}
              />
            </Form.Item>
          </QaClaimsSnapshot>
        </Col>

        <Col span={3}>
          <Form.Item
            noStyle
            shouldUpdate={(prevValue, currentVal) => {
              return (
                prevValue[label]?.isAttribute !== currentVal[label]?.isAttribute
              );
            }}
          >
            {({ getFieldValue }) => {
              const attribute = getFieldValue([label, 'isAttribute']);

              const disabled = !isEdit || !attribute;

              return (
                <QaClaimsSnapshot
                  isToggleSnapshot={isToggleSnapshot}
                  supplierModifiedValue={
                    supplierModifiedValue?.willCustomerFacing
                  }
                  snapshotValue={snapshotValue?.willCustomerFacing}
                  qaSpecType={productType}
                  productId={productId}
                  highlightContent={
                    <HighlightContent
                      label='Will claim be customer facing?:'
                      value={snapshotValue?.willCustomerFacing}
                      variant='checkbox-true-false'
                    />
                  }
                >
                  <Form.Item
                    name={[label, 'willCustomerFacing']}
                    wrapperCol={{ span: 24 }}
                  >
                    <PrimaryFieldSnapshot
                      disabled={disabled}
                      variant='checkbox-true-false'
                    />
                  </Form.Item>
                </QaClaimsSnapshot>
              );
            }}
          </Form.Item>
        </Col>

        <Col span={3}>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues[label]?.isAttribute !==
              currentValues[label]?.isAttribute
            }
          >
            {({ getFieldValue }) => {
              const isAttribute = getFieldValue([
                label,
                'isAttribute',
              ]);
              const disabled = !isEdit || !isAttribute;
              // if (!willCustomerFacing) {
              //   const current = getFieldValue(label) || {};
              //   formInst.setFieldsValue({
              //     [label]: {
              //       ...current,
              //       isThirdParty: null,
              //     },
              //   });
              // }

              return (
                <QaClaimsSnapshot
                  isToggleSnapshot={isToggleSnapshot}
                  supplierModifiedValue={supplierModifiedValue?.isThirdParty}
                  snapshotValue={snapshotValue?.isThirdParty}
                  qaSpecType={productType}
                  productId={productId}
                  highlightContent={
                    <HighlightContent
                      label='Is 3rd party certified?:'
                      value={snapshotValue?.isThirdParty}
                      variant='checkbox-true-false'
                    />
                  }
                >
                  <Form.Item
                    name={[label, 'isThirdParty']}
                    wrapperCol={{ span: 24 }}
                  >
                    <PrimaryFieldSnapshot
                      disabled={disabled}
                      variant='checkbox-true-false'
                      onChange={(e) => {
                        const checked = e.target.value;
                        if (checked) {
                          invalidateReadyToUseClaims();
                          fetchReadyToUseClaim().then(() =>
                            setApplyTick((t) => t + 1)
                          );
                        } else {
                          const current = formInst.getFieldValue(label) || {};
                          formInst.setFieldsValue({
                            [label]: {
                              ...current,
                              certifierName: null,
                              certificationExpiration: null,
                              certificateUrl: null,
                              certificateThumbSizeFull: null,
                            },
                          });
                        }
                      }}
                    />
                  </Form.Item>
                </QaClaimsSnapshot>
              );
            }}
          </Form.Item>
          {isFetchingReadyClaim && (
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Fetching latest certificate details...
            </div>
          )}
        </Col>

        <Col span={3}>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              return (
                <Form.Item
                  wrapperCol={{ span: 24 }}
                  name={[label, 'certifierName']}
                >
                  <Input.TextArea
                    className='cert-name-input'
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    readOnly
                    bordered={false}
                    placeholder="N/A"
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
        <Col span={3} style={{ textAlign: 'center' }}>
          <Form.Item noStyle
            shouldUpdate={(prev, curr) =>
              prev[label]?.certificationExpiration !== curr[label]?.certificationExpiration ||
              prev[label]?.isThirdParty !== curr[label]?.isThirdParty
            }
          >
            {({ getFieldValue }) => {
              const claim = getFieldValue(label) || {};
              let certificationExpiration = claim?.isThirdParty
                ? claim?.certificationExpiration
                : null;

              certificationExpiration = certificationExpiration
                ? addUtcForIsoString(certificationExpiration)
                : null;

              certificationExpiration = certificationExpiration
                ? formatMDY(moment(certificationExpiration))
                : null;
              const value = claim.certificationExpiration
                ? formatMDY(
                  moment(addUtcForIsoString(claim.certificationExpiration))
                )
                : null;
              return (
                <Form.Item name={[label, 'certificationExpiration']} style={{ color: value ? '#000' : '#bfbfbf' }}>
                  <span>{value || 'N/A'}</span>
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
        <Col span={2} >
          <Form.Item noStyle shouldUpdate >
            {({ getFieldValue }) => {
              const claim = getFieldValue(label) || {};
              return (
                <Form.Item name={[label, 'certificateUrl']} noStyle>
                  {claim.certificateUrl ? (
                    <CellRenderActionClaims
                      data={claim}
                      setStateActions={(updater) => {
                        if (typeof updater === 'function') return setStateActions(updater);
                        setStateActions((s) => ({
                          ...s,
                          visible: true,
                          filedata: claim,
                        }));
                      }}
                    />
                  ) : (
                    <span style={{ color: '#bfbfbf' }}>N/A</span>
                  )}
                  {certificateCount > 1 && claim.certificateUrl && (
                    <Tooltip title={`There are ${certificateCount - 1} more certificates available for ${label} claim`}>
                      <InfoCircleOutlined style={{ color: "#1890ff", cursor: "pointer", marginLeft: "10px" }} />
                    </Tooltip>
                  )}
                </Form.Item>
              );
            }}
          </Form.Item>
        </Col>
        <Col span={3}>
          <QaClaimsSnapshot
            isToggleSnapshot={isToggleSnapshot}
            supplierModifiedValue={supplierModifiedValue?.additionalInformation}
            snapshotValue={snapshotValue?.additionalInformation}
            qaSpecType={productType}
            productId={productId}
            highlightContent={
              <HighlightContent
                label='Additional Information:'
                value={snapshotValue?.additionalInformation}
                variant='text'
              />
            }
          >
            <Form.Item
              wrapperCol={{ span: 24 }}
              name={[label, 'additionalInformation']}
            >
              <PrimaryFieldSnapshot
                autoSize={{ minRows: 1, maxRows: 6 }}
                maxLength={1000}
                placeholder='Max 1000 characters'
                variant='textarea'
                disabled={!isEdit}
              />
            </Form.Item>
          </QaClaimsSnapshot>
        </Col>
      </>
    );
  }
);

const normalizeExt = (ext) => {
  if (!ext) return undefined;
  const e = String(ext).toLowerCase();
  if (e === 'jpg') return 'jpeg';
  if (e === 'htm') return 'html';
  return e;
};

const extractExtFromUrl = (url) => {
  if (!url) return undefined;
  const clean = url.split('?')[0];

  const match = clean.match(/\/(?:f|full)\/([^/]+)$/i);
  if (match && match[1]) return normalizeExt(match[1]);

  const segs = clean.split('/');
  const beforeSpecial = segs.findLastIndex(
    (s) => s.toLowerCase() === 'f' || s.toLowerCase() === 'full'
  );
  if (beforeSpecial > 0) {
    const filename = segs[beforeSpecial - 1];
    const dot = filename.lastIndexOf('.');
    if (dot !== -1) return normalizeExt(filename.slice(dot + 1));
  }

  return undefined;
};

const ClaimOption = memo(({ value, label }) => {
  return (
    <Select.Option value={value} key={value}>
      {label}
    </Select.Option>
  );
});

const HeaderClaim = ({ text, style }) => {
  return (
    <Typography.Title level={5} style={{ ...style, fontSize: 14, color: '#595959' }}>
      {text}
    </Typography.Title>
  );
};

export default QaClaims;